import { z } from "zod";

// =============================================================================
// SHARED ENUMS
// =============================================================================

// Currency
export const monedaSchema = z.enum(["PEN", "USD", "EUR"]);
export type Moneda = z.infer<typeof monedaSchema>;
export const MONEDAS = monedaSchema.options;

// Periodicity
export const periodicidadSchema = z.enum([
	"unica",
	"mensual",
	"bimestral",
	"trimestral",
	"semestral",
	"anual",
]);
export type Periodicidad = z.infer<typeof periodicidadSchema>;
export const PERIODICIDADES = periodicidadSchema.options;

// Interest rate period
export const periodoTasaSchema = z.enum(["anual", "mensual", "diaria"]);
export type PeriodoTasa = z.infer<typeof periodoTasaSchema>;

// Insurance types
export const tipoSeguroSchema = z.enum(["porcentaje", "monto"]);
export type TipoSeguro = z.infer<typeof tipoSeguroSchema>;

// Property status
export const propiedadEstadoSchema = z.enum([
	"activa",
	"hipotecada",
	"pagado",
	"inactiva",
]);
export type PropiedadEstado = z.infer<typeof propiedadEstadoSchema>;

// Mortgage status
export const estadoHipotecaSchema = z.enum([
	"activa",
	"inactiva",
	"pagada",
	"refinanciada",
	"compra_deuda",
	"archivada",
]);
export type EstadoHipoteca = z.infer<typeof estadoHipotecaSchema>;

// Credit types
export const tipoCreditoSchema = z.enum(["mi_vivienda", "tradicional", "otro"]);
export type TipoCredito = z.infer<typeof tipoCreditoSchema>;

// Reduction types for amortization
export const tipoReduccionSchema = z.enum(["cuota", "plazo", "pago_total"]);
export type TipoReduccion = z.infer<typeof tipoReduccionSchema>;

// Valuation sources
export const fuenteValorizacionSchema = z.enum([
	"compra",
	"tasacion",
	"manual",
	"plusvalia",
	"mercado",
	"otro",
]);
export type FuenteValorizacion = z.infer<typeof fuenteValorizacionSchema>;
export const FUENTES_VALORIZACION = fuenteValorizacionSchema.options;

// Expense types
export const tipoGastoSchema = z.enum([
	"costos_de_compra",
	"mantenimiento",
	"predial",
	"arbitrios",
	"seguro_vida",
	"agente_inmobiliario",
	"remodelacion",
	"implementacion",
	"reparacion",
	"otros",
]);
export type TipoGasto = z.infer<typeof tipoGastoSchema>;
export const TIPOS_GASTO = tipoGastoSchema.options;

// Income types
export const tipoIngresoSchema = z.enum(["alquiler", "alquiler_temporal", "venta", "otro"]);
export type TipoIngreso = z.infer<typeof tipoIngresoSchema>;
export const TIPOS_INGRESO = tipoIngresoSchema.options;

// Property types
export const tipoPropiedadSchema = z.enum([
	"casa",
	"departamento",
	"terreno",
	"local_comercial",
	"oficina",
	"otro",
]);
export type TipoPropiedad = z.infer<typeof tipoPropiedadSchema>;
export const TIPOS_PROPIEDAD = tipoPropiedadSchema.options;

// Property view
export const vistaPropiedadSchema = z.enum(["interior", "exterior", "ambas"]);
export type VistaPropiedad = z.infer<typeof vistaPropiedadSchema>;
export const VISTAS_PROPIEDAD = vistaPropiedadSchema.options;

// Bonus types
export const tipoBonoSchema = z.enum([
	"buen_pagador",
	"verde",
	"integrador",
	"otro",
	"no_bono",
]);
export type TipoBono = z.infer<typeof tipoBonoSchema>;

// Furnishing levels
export const nivelAmobladoSchema = z.enum([
	"no_amoblado",
	"semi_amoblado",
	"amoblado",
]);
export type NivelAmoblado = z.infer<typeof nivelAmobladoSchema>;
export const NIVELES_AMOBLADO = nivelAmobladoSchema.options;

// Construction status
export const estadoConstruccionSchema = z.enum([
	"planos",
	"en_construccion",
	"terminado",
]);
export type EstadoConstruccion = z.infer<typeof estadoConstruccionSchema>;
export const ESTADOS_CONSTRUCCION = estadoConstruccionSchema.options;

// Subscription status
export const estadoSuscripcionSchema = z.enum([
	"activa",
	"cancelada",
	"vencida",
	"prueba",
]);
export type EstadoSuscripcion = z.infer<typeof estadoSuscripcionSchema>;

// Simulation types
export const tipoSimulacionSchema = z.enum([
	"hipoteca",
	"propiedad",
	"rentabilidad",
]);
export type TipoSimulacion = z.infer<typeof tipoSimulacionSchema>;

// =============================================================================
// SHARED PRIMITIVES
// =============================================================================

/**
 * Optional number that coerces string/number to number or undefined.
 * Use for numeric form inputs that may be empty. Handles null from API.
 */
export const optionalNumber = z
	.union([z.number(), z.string(), z.nan(), z.null()])
	.transform((val) => {
		if (val === "" || val === null || val === undefined) return undefined;
		const num = Number(val);
		return isNaN(num) ? undefined : num;
	})
	.optional();

/**
 * Required positive number that coerces string/number to number.
 * Use for required numeric fields like prices.
 * Uses z.coerce to ensure consistent input/output types for react-hook-form.
 */
export const requiredPositiveNumber = z.coerce
	.number({
		invalid_type_error: "required_number",
		required_error: "required_number",
	})
	.refine((val) => !isNaN(val) && val > 0, {
		message: "required_positive",
	});

/**
 * Optional date string. Use for date inputs. Transforms empty strings and null to undefined.
 */
export const optionalDateString = z
	.string()
	.nullable()
	.optional()
	.transform((val) => (val === "" || val === null ? undefined : val));

/**
 * Required date string. Use for required date inputs.
 */
export const requiredDateString = z
	.string({
		required_error: "required_date",
		invalid_type_error: "required_date",
	})
	.min(1, "required_date");

/**
 * Optional string that transforms empty strings and null to undefined.
 */
export const optionalString = z
	.string()
	.nullable()
	.optional()
	.transform((val) => (val === "" || val === null ? undefined : val));

/**
 * Required non-empty string.
 */
export const requiredString = z
	.string({
		required_error: "required",
		invalid_type_error: "required",
	})
	.min(1, "required");

// =============================================================================
// SHARED API SCHEMAS
// =============================================================================

/**
 * Timestamp fields for API responses.
 */
export const timestampFieldsSchema = z.object({
	created_at: z.string(),
	updated_at: z.string(),
});
export type TimestampFields = z.infer<typeof timestampFieldsSchema>;

/**
 * Pagination metadata for paginated responses.
 */
export const paginationSchema = z.object({
	total_count: z.number(),
	limit: z.number(),
	skip: z.number(),
});
export type PaginationMetadata = z.infer<typeof paginationSchema>;

/**
 * Creates a paginated response schema for a given item schema.
 */
export const createPaginatedSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
	z.object({
		pagination: paginationSchema,
		data: z.array(itemSchema),
	});

/**
 * Base list params schema for API queries.
 */
export const listParamsSchema = z.object({
	limit: z.number().optional(),
	skip: z.number().optional(),
	sort_by: z.string().optional(),
	sort_direction: z.enum(["ASC", "DESC"]).optional(),
});
export type ListParams = z.infer<typeof listParamsSchema>;

// =============================================================================
// VALIDATION UTILITIES (re-exported for convenience)
// =============================================================================

export type { ApiValidationError } from "./validation";
export { mapApiErrorToFieldPath, handleApiErrors } from "./validation";
